import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import {Icon} from '@/components/atoms';
import ShimmerTableRow from '@/components/atoms/ShimmerTableRow';
import EmptyState from '@/components/molecules/EmptyState/EmptyState';
import {useCustomDataTable} from '@/hooks/useCustomDataTable-hook';
import DataTableBody from './DataTableBody';
import DataTableFilterRow from './DataTableFilterRow';
import DataTableHead from './DataTableHead';
import DataTablePagination from './DataTablePagination';
import DataTableToolbar from './DataTableToolbar';
import DateRangeCustomInput from './DateRangeCustomInput';
import SelectionSummary from './SelectionSummary';
import SingleDateCustomInput from './SingleDateCustomInput';

import {Paper, Table, TableContainer, TableHead} from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDataTable.scss';

// Wrapper components moved out of render to avoid redefining on each render
function DateRangeCustomInputWrapper(props) {
  // dateRanges and setOpenDatePicker will be injected via closure from CustomDataTable
  // We'll assign these as variables below
  return (
    <DateRangeCustomInput
      {...props}
      dateRanges={DateRangeCustomInputWrapper.dateRanges}
      setOpenDatePicker={DateRangeCustomInputWrapper.setOpenDatePicker}
    />
  );
}

function SingleDateCustomInputWrapper(props) {
  return (
    <SingleDateCustomInput
      {...props}
      singleDates={SingleDateCustomInputWrapper.singleDates}
      setOpenDatePicker={SingleDateCustomInputWrapper.setOpenDatePicker}
    />
  );
}

const CustomDataTable = forwardRef(
  (
    {
      // Core data props
      columns = [],
      records = [],
      isLoading = false,
      pageSize = 10,
      totalRecords = 0,

      // Grouped config objects
      paginationConfig = {},
      selectionConfig = {},
      sortingConfig = {},
      filterConfig = {},
      toolbarConfig = {},
      displayConfig = {}
    },
    ref
  ) => {
    // Destructure config objects with defaults
    const {
      pageIndex = 0,
      setPageIndex = () => {},
      firstPageIndex = null,
      hidePagination = false
    } = paginationConfig;

    const {
      onRowSelect = () => {},
      isSelectAll = false,
      setIsSelectAll = () => {},
      initialRowSelection = [],
      hideSelectAll = false,
      hideCheckbox = false,
      showSelectionSummary = false,
      hideSelectionSummary = false,
      initialSelectedCount = 0,
      disableSelectionPersistence = false,
      globalSelectionEnable = false,
      resetSelectionOnFilter = true,
      isEdit
    } = selectionConfig;

    const {handleSorting = () => {}, initialSortConfig} = sortingConfig;

    const {
      setSearchArry = () => {},
      onResetAll = () => {},
      hideFilterRow = false,
      hideResetButton = false,
      disabledHeaders = []
    } = filterConfig;

    const {
      hideToolbar = false,
      toolbarActions = [],
      showDownload = false,
      onDownload = () => {},
      showCreateNew = false,
      onCreateNew = null,
      showAddStores = false,
      onAddStores = () => {},
      showMoveStores = false,
      onMoveStores = () => {},
      showActionMenu = false,
      actionItems = [],
      onActionClick = () => {}
    } = toolbarConfig;

    const {
      wrapperClassName = '',
      tableMinWidth = null,
      tableMinHeight = null,
      emptyStateConfig = {},
      onNameClick = () => {}
    } = displayConfig;

    const {
      dateRanges,
      singleDates,
      openDatePicker,
      selectedRows,
      globallySelectedRows,
      globalSelectAll,
      allFilteredSelected,
      selectAllTotal,
      sortConfig,
      filter,
      totalPages,
      partialSelectAll,
      deselectedItems,
      setOpenDatePicker,
      handleResetAll,
      handleSort,
      handleSelectAll,
      handleRowSelect,
      handleFilterChange,
      handleDateRangeChange,
      handleSingleDateChange,
      debouncedIsLoading,
      setDebouncedIsLoading
    } = useCustomDataTable({
      columns,
      initialSortConfig,
      records,
      pageSize,
      onResetAll,
      onRowSelect,
      handleSorting,
      setSearchArry,
      setPageIndex,
      isSelectAll,
      totalRecords,
      pageIndex,
      initialRowSelection,
      firstPageIndex,
      setIsSelectAll,
      disableSelectionPersistence,
      resetSelectionOnFilter,
      isEdit
    });

    useImperativeHandle(ref, () => ({
      handleResetAll
    }));

    // Inject latest props into wrapper components via static assignment
    DateRangeCustomInputWrapper.dateRanges = dateRanges;
    DateRangeCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;
    SingleDateCustomInputWrapper.singleDates = singleDates;
    SingleDateCustomInputWrapper.setOpenDatePicker = setOpenDatePicker;

    let selectedCount;
    if (partialSelectAll) {
      selectedCount = selectAllTotal - deselectedItems.size;
    } else if (isSelectAll || globalSelectAll || allFilteredSelected) {
      selectedCount = totalRecords;
    } else {
      selectedCount = disableSelectionPersistence
        ? globallySelectedRows?.size || selectedRows?.length || 0
        : initialSelectedCount ||
          globallySelectedRows?.size ||
          selectedRows?.length ||
          0;
    }

    const getToolbarActions = useCallback(() => {
      const baseActions = [...toolbarActions];
      if (showCreateNew) {
        baseActions.push({
          id: 'create-new',
          label: 'Create New',
          onClick: onCreateNew,
          icon: <Icon icon='add' size='small' />,
          variant: 'secondary',
          isDark: true
        });
      }
      if (showDownload) {
        baseActions.push({
          id: 'download',
          label: 'Download',
          onClick: () => {
            // When select all is active (with or without filters), pass empty array
            // so API returns all filtered records. Otherwise pass selected row IDs.
            const isAllSelected =
              globalSelectAll || allFilteredSelected || partialSelectAll;
            const excludeIds = Array.from(deselectedItems || new Set());
            // Use globallySelectedRows if globalSelectionEnable is true, otherwise use selectedRows (current page only)
            const rowsToDownload = globalSelectionEnable
              ? Array.from(globallySelectedRows)
              : selectedRows;
            onDownload(isAllSelected ? [] : rowsToDownload, {
              isAllSelected,
              excludeIds
            });
          },
          icon: <Icon icon='download' size='small' />,
          variant: 'outlined',
          isDark: true,
          requiresSelection: true
        });
      }
      if (showAddStores) {
        baseActions.push({
          id: 'add-stores',
          label: 'Add Stores',
          onClick: onAddStores,
          icon: <Icon icon='add_box' size='large' />,
          variant: 'secondary',
          isDark: true,
          requiresSelection: false,
          disabled: false
        });
      }
      if (showMoveStores) {
        baseActions.push({
          id: 'move-stores',
          label: 'Move Stores',
          onClick: onMoveStores,
          variant: 'secondary',
          isDark: true,
          disabled: true,
          requiresSelection: true
        });
      }
      return baseActions;
    }, [
      toolbarActions,
      showCreateNew,
      onCreateNew,
      showDownload,
      globalSelectAll,
      allFilteredSelected,
      partialSelectAll,
      onDownload,
      selectedRows,
      globallySelectedRows,
      globalSelectionEnable,
      deselectedItems,
      showAddStores,
      onAddStores,
      showMoveStores,
      onMoveStores
    ]);

    const handleActionMenuClick = useCallback(
      (action) => {
        const actionText = (
          action?.displayText ||
          action?.label ||
          ''
        ).toString();
        if (
          actionText.toLowerCase() === 'download' &&
          typeof onDownload === 'function'
        ) {
          const isAllSelected =
            globalSelectAll || allFilteredSelected || partialSelectAll;
          const excludeIds = Array.from(deselectedItems || new Set());
          const rowsToDownload = globalSelectionEnable
            ? Array.from(globallySelectedRows)
            : selectedRows;

          onDownload(isAllSelected ? [] : rowsToDownload, {
            isAllSelected,
            excludeIds
          });
          return;
        }

        onActionClick(action);
      },
      [
        onActionClick,
        onDownload,
        globalSelectAll,
        allFilteredSelected,
        partialSelectAll,
        deselectedItems,
        globalSelectionEnable,
        globallySelectedRows,
        selectedRows
      ]
    );

    // Extract page calculation
    const calculatedPage = useMemo(() => {
      if (firstPageIndex == 1) {
        return pageIndex;
      } else if (pageIndex >= 0) {
        return pageIndex + 1;
      } else {
        return 1;
      }
    }, [firstPageIndex, pageIndex]);

    useEffect(() => {
      if (isLoading) {
        setDebouncedIsLoading(true);
      } else {
        const timer = setTimeout(() => {
          setDebouncedIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
      }
    }, [isLoading, setDebouncedIsLoading]);

    return (
      <div data-testid='custom-datatable' className={wrapperClassName}>
        {!hideToolbar && (
          <DataTableToolbar
            onResetAll={handleResetAll}
            selectedRowsCount={selectedCount}
            showActionMenu={showActionMenu}
            actionItems={actionItems}
            onActionClick={handleActionMenuClick}
            toolbarActions={getToolbarActions()}
            hideResetButton={hideResetButton}
          />
        )}
        {!hideSelectionSummary && (
          <SelectionSummary
            selectedCount={selectedCount}
            showSelectionSummary={showSelectionSummary}
          />
        )}
        <TableContainer
          component={Paper}
          className={`table-scroll-container${records.length > 5 ? ' table-scroll-container--minheight' : ''}`}>
          <Table
            className='dark-mode-table'
            style={
              tableMinWidth || tableMinHeight
                ? {
                    ...(tableMinWidth ? {minWidth: tableMinWidth} : {}),
                    ...(tableMinHeight ? {minHeight: tableMinHeight} : {})
                  }
                : undefined
            }>
            <TableHead>
              <DataTableHead
                columns={columns}
                sortConfig={sortConfig}
                handleSort={handleSort}
                isSelectAll={isSelectAll}
                records={records}
                selectedRows={selectedRows}
                handleSelectAll={handleSelectAll}
                partialSelectAll={partialSelectAll}
                deselectedItems={deselectedItems}
                disabledHeaders={disabledHeaders}
                hideSelectAll={hideSelectAll}
                hideCheckbox={hideCheckbox}
                initialSelectedCount={initialSelectedCount}
                totalRecords={totalRecords}
                disableSelectionPersistence={disableSelectionPersistence}
              />
              {!hideFilterRow && (
                <DataTableFilterRow
                  columns={columns}
                  dateRanges={dateRanges}
                  singleDates={singleDates}
                  openDatePicker={openDatePicker}
                  filter={filter}
                  setOpenDatePicker={setOpenDatePicker}
                  handleDateRangeChange={handleDateRangeChange}
                  handleSingleDateChange={handleSingleDateChange}
                  handleFilterChange={handleFilterChange}
                  DateRangeCustomInput={DateRangeCustomInputWrapper}
                  SingleDateCustomInput={SingleDateCustomInputWrapper}
                />
              )}
            </TableHead>
            <DataTableBody
              columns={columns}
              records={records}
              isLoading={debouncedIsLoading}
              selectedRows={selectedRows}
              handleRowSelect={handleRowSelect}
              handleResetAll={handleResetAll}
              onNameClick={onNameClick}
              EmptyState={EmptyState}
              ShimmerTableRow={ShimmerTableRow}
              partialSelectAll={partialSelectAll}
              deselectedItems={deselectedItems}
              hideSelectAll={hideSelectAll}
              hideCheckbox={hideCheckbox}
              emptyStateConfig={emptyStateConfig}
            />
          </Table>
        </TableContainer>
        {!hidePagination && (
          <div className='sticky-pagination'>
            <DataTablePagination
              totalPages={
                Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1
              }
              page={calculatedPage}
              handlePageChange={(value) => {
                if (firstPageIndex == 1) {
                  setPageIndex(value);
                } else {
                  setPageIndex(value - 1);
                }
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

CustomDataTable.propTypes = {
  // Core data props
  columns: PropTypes.arrayOf(PropTypes.object),
  records: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  pageSize: PropTypes.number,
  totalRecords: PropTypes.number,

  // Config object props
  paginationConfig: PropTypes.shape({
    pageIndex: PropTypes.number,
    setPageIndex: PropTypes.func,
    firstPageIndex: PropTypes.number,
    hidePagination: PropTypes.bool
  }),
  selectionConfig: PropTypes.shape({
    onRowSelect: PropTypes.func,
    isSelectAll: PropTypes.bool,
    setIsSelectAll: PropTypes.func,
    initialRowSelection: PropTypes.array,
    hideSelectAll: PropTypes.bool,
    hideCheckbox: PropTypes.bool,
    showSelectionSummary: PropTypes.bool,
    hideSelectionSummary: PropTypes.bool,
    initialSelectedCount: PropTypes.number,
    disableSelectionPersistence: PropTypes.bool,
    globalSelectionEnable: PropTypes.bool,
    resetSelectionOnFilter: PropTypes.bool,
    isEdit: PropTypes.bool
  }),
  sortingConfig: PropTypes.shape({
    handleSorting: PropTypes.func,
    initialSortConfig: PropTypes.object
  }),
  filterConfig: PropTypes.shape({
    setSearchArry: PropTypes.func,
    onResetAll: PropTypes.func,
    hideFilterRow: PropTypes.bool,
    hideResetButton: PropTypes.bool,
    disabledHeaders: PropTypes.arrayOf(PropTypes.string)
  }),
  toolbarConfig: PropTypes.shape({
    hideToolbar: PropTypes.bool,
    toolbarActions: PropTypes.arrayOf(PropTypes.object),
    showDownload: PropTypes.bool,
    onDownload: PropTypes.func,
    showCreateNew: PropTypes.bool,
    onCreateNew: PropTypes.func,
    showAddStores: PropTypes.bool,
    onAddStores: PropTypes.func,
    showMoveStores: PropTypes.bool,
    onMoveStores: PropTypes.func,
    showActionMenu: PropTypes.bool,
    actionItems: PropTypes.arrayOf(PropTypes.object),
    onActionClick: PropTypes.func
  }),
  displayConfig: PropTypes.shape({
    wrapperClassName: PropTypes.string,
    tableMinWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tableMinHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    emptyStateConfig: PropTypes.object,
    onNameClick: PropTypes.func
  })
};

CustomDataTable.displayName = 'CustomDataTable';

export default React.memo(CustomDataTable);
